import { Loading3QuartersOutlined } from '@ant-design/icons';
import {
  EnumRichiestaDTOStato,
  useGetFrontRichiesteId,
  useGetFrontTipiDomandaId,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { selectTranslatedInquiryType } from '@framework/common-web';
import { SubmissionLogic } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import {
  BrandedPath,
  OnPageResultContainer,
  ResponsiveCard,
  generatePath,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
} from '@framework/ui';
import { Alert, Result } from 'antd';
import { useCallback, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { PageLayout } from '../../ui/layout/PageLayout';
import { LoadingPage } from '../loading/LoadingPage';

const IconColorStyle = styled.span`
  color: ${props => props.theme.colorSuccess};
`;

interface Params {
  id: string;
}

export interface ISubmissionResultPageProps
  extends RouteComponentProps<Params> {}

/**
 * Pagina di riepilogo che mostra lo stato della richiesta una volta inviata.
 * Può mostrare il risultato dopo l'invio o lo stato di invio in corso.
 */
export function SubmissionResultPage(props: ISubmissionResultPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');

  const history = useHistory();
  const { t } = useTranslation();

  const {
    data: submission,
    isLoading,
    error,
  } = useGetFrontRichiesteId(submissionId!, {
    query: { keepPreviousData: true, enabled: Boolean(submissionId) },
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const inquiryRequest = useGetFrontTipiDomandaId(submission?.idTipoDomanda!, {
    query: {
      keepPreviousData: true,
      enabled: Boolean(submission?.idTipoDomanda),
      select: selectTranslatedInquiryType,
    },
  });

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-form.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: inquiryRequest.data?.titolo ?? '',
  });

  const sentLayout = useMemo(
    () => inquiryRequest.data?.postCompilazione?.inviato,
    [inquiryRequest.data?.postCompilazione?.inviato],
  );
  const theme = useTheme();

  const handlePrev = useCallback(() => {
    // TODO
    console.log('TODO');
  }, []);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={getNetworkErrorMessage(
            error,
            t(
              'submission-form.errore-in-caricamento-richiesta',
              'Si è verificato un errore durante il caricamento della richiesta.',
            ),
          )}
        />
      </PageLayout>
    );
  }

  if (!submissionId) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={t(
            'submission-form.id-della-richiesta-non-valido',
            'ID della richiesta non valido',
          )}
        />
      </PageLayout>
    );
  }

  if (SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/inviata', { id: submissionId }),
    );
  }

  return (
    <PageLayout>
      <ResponsiveCard>
        <OnPageResultContainer>
          {submission?.stato === EnumRichiestaDTOStato.IN_INVIO ? (
            <Result
              icon={
                <Loading3QuartersOutlined
                  spin
                  style={{ color: theme.colorSuccess }}
                  aria-label="Invio in corso..."
                />
              }
              title={t('submission-form.invio-in-corso', 'Invio in corso...')}
            />
          ) : (
            <span /> // TODO
          )}
        </OnPageResultContainer>
      </ResponsiveCard>
    </PageLayout>
  );
}
